
import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { signIn, signUp, signOut } from "../services/auth";
import { useAuthState } from "../hooks/useAuthState";
import { AuthContextType } from "../types/auth";
import { useUserRoles, UserRole } from "@/hooks/useUserRoles";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { user, session, isLoading: authLoading, isSupabaseReady, refreshUser } = useAuthState();
  const { roles, hasRole, addRole, removeRole, isLoading: isLoadingRoles } = useUserRoles({ 
    userId: user?.id 
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  const login = async (email: string, password: string) => {
    try {
      await signIn(email, password);
    } catch (error: any) {
      console.error("Error during login:", error.message);
      throw error;
    }
  };

  const signup = async (email: string, password: string, name: string, companyName: string, logoFile?: File) => {
    try {
      await signUp(email, password, name, companyName, logoFile);
    } catch (error: any) {
      console.error("Error during signup:", error.message);
      throw error;
    }
  };

  // Função específica para limpeza do Huggy durante logout
  const cleanupHuggyOnLogout = () => {
    console.log("🧹 INICIANDO LIMPEZA DO HUGGY NO LOGOUT");
    
    try {
      // Remover todos os scripts do Huggy
      const existingScripts = document.querySelectorAll('script[src*="huggy"], script[src*="pwz"], script[src*="widget.min.js"]');
      existingScripts.forEach(script => {
        console.log("🗑️ Removendo script Huggy:", (script as HTMLScriptElement).src);
        script.remove();
      });
      
      // Remover TODOS os elementos DOM relacionados ao Huggy
      const huggySelectors = [
        '[id*="huggy"]', '[class*="huggy"]', 'iframe[src*="huggy"]', 
        '[id*="pwz"]', '[class*="pwz"]', '[data-huggy]',
        '.huggy-widget', '#huggy-chat', '.huggy-launcher'
      ];
      
      huggySelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          console.log("🗑️ Removendo elemento Huggy:", el.tagName, el.className || el.id);
          el.remove();
        });
      });
      
      // Limpar variáveis globais COMPLETAMENTE
      if (window.$_Huggy) {
        console.log("🗑️ Limpando $_Huggy:", window.$_Huggy);
        delete window.$_Huggy;
      }
      if (window.pwz) {
        console.log("🗑️ Limpando pwz:", window.pwz);
        delete window.pwz;
      }
      
      // Limpar TODOS os dados relacionados ao Huggy (localStorage/sessionStorage/IndexedDB)
      try {
        // Padrões mais específicos para dados do Huggy
        const huggyPatterns = ['huggy', 'pwz', 'chat-widget', 'widget-'];
        
        // Chaves específicas conhecidas do Huggy
        const specificHuggyKeys = ['huggy_widget_configs', 'huggy_session', 'huggy_user_data', 'huggy_conversation'];
        
        // Remover chaves específicas primeiro
        specificHuggyKeys.forEach(key => {
          if (localStorage.getItem(key) !== null) {
            console.log("🗑️ Removendo localStorage específico:", key);
            localStorage.removeItem(key);
          }
          if (sessionStorage.getItem(key) !== null) {
            console.log("🗑️ Removendo sessionStorage específico:", key);
            sessionStorage.removeItem(key);
          }
        });
        
        // Depois remover por padrões
        Object.keys(localStorage).forEach(key => {
          if (huggyPatterns.some(pattern => key.toLowerCase().includes(pattern))) {
            console.log("🗑️ Removendo localStorage:", key);
            localStorage.removeItem(key);
          }
        });
        
        Object.keys(sessionStorage).forEach(key => {
          if (huggyPatterns.some(pattern => key.toLowerCase().includes(pattern))) {
            console.log("🗑️ Removendo sessionStorage:", key);
            sessionStorage.removeItem(key);
          }
        });

        // Limpar IndexedDB do Huggy (se existir)
        if ('indexedDB' in window) {
          console.log("🗑️ Verificando IndexedDB para dados do Huggy...");
          
          // Tentar deletar possíveis databases do Huggy
          const huggyDBNames = ['huggy', 'pwz', 'huggy-chat', 'huggy_db'];
          huggyDBNames.forEach(dbName => {
            try {
              const deleteReq = indexedDB.deleteDatabase(dbName);
              deleteReq.onsuccess = () => console.log(`🗑️ IndexedDB '${dbName}' removido`);
              deleteReq.onerror = () => console.log(`⚠️ Erro ao remover IndexedDB '${dbName}'`);
            } catch (e) {
              console.log(`⚠️ Erro ao tentar remover IndexedDB '${dbName}':`, e);
            }
          });
        }

        // Limpar possível Cache API do Huggy
        if ('caches' in window) {
          console.log("🗑️ Verificando Cache API para dados do Huggy...");
          caches.keys().then(cacheNames => {
            cacheNames.forEach(cacheName => {
              if (huggyPatterns.some(pattern => cacheName.toLowerCase().includes(pattern))) {
                console.log("🗑️ Removendo cache:", cacheName);
                caches.delete(cacheName);
              }
            });
          }).catch(e => console.log("⚠️ Erro ao limpar caches:", e));
        }

      } catch (e) {
        console.log("⚠️ Erro ao limpar storage:", e);
      }
      
      // Limpar possíveis cookies do Huggy
      try {
        const cookies = document.cookie.split(";");
        cookies.forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name.toLowerCase().includes('huggy') || name.toLowerCase().includes('pwz')) {
            console.log("🗑️ Removendo cookie:", name);
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
          }
        });
      } catch (e) {
        console.log("⚠️ Erro ao limpar cookies:", e);
      }
      
      console.log("✅ LIMPEZA DO HUGGY NO LOGOUT FINALIZADA");
      
    } catch (error) {
      console.error("❌ Erro durante limpeza do Huggy:", error);
    }
  };

  const logout = async () => {
    console.log("🚪 INICIANDO PROCESSO DE LOGOUT");
    
    try {
      // LIMPAR HUGGY ANTES DE QUALQUER COISA
      console.log("🧹 EXECUTANDO LIMPEZA DO HUGGY ANTES DO LOGOUT");
      cleanupHuggyOnLogout();
      
      // Aguardar um pouco para garantir que a limpeza foi executada
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Attempt to sign out via Supabase
      await signOut();
      
      // Toast notification for successful logout
      toast({
        title: "Logout bem-sucedido",
        description: "Você foi desconectado com sucesso.",
      });
    } catch (error: any) {
      console.error("Error logging out:", error);
      
      // For session-related errors, show a gentler message
      if (error.message?.includes('Auth session missing') || 
          error.message?.includes('Forbidden')) {
        toast({
          title: "Sessão encerrada",
          description: "Você foi redirecionado para a página de login.",
        });
      } else {
        toast({
          title: "Falha ao desconectar",
          description: "Houve um erro, mas você foi redirecionado para a página de login.",
          variant: "destructive",
        });
      }
    } finally {
      // VERIFICAR SE A LIMPEZA FOI EFETIVA ANTES DO REDIRECIONAMENTO
      console.log("🔍 VERIFICANDO SE A LIMPEZA FOI EFETIVA:");
      console.log("localStorage restante:", Object.keys(localStorage).filter(key => 
        key.toLowerCase().includes('huggy') || key.toLowerCase().includes('pwz')
      ));
      console.log("sessionStorage restante:", Object.keys(sessionStorage).filter(key => 
        key.toLowerCase().includes('huggy') || key.toLowerCase().includes('pwz')
      ));
      console.log("$_Huggy ainda existe?", !!window.$_Huggy);
      console.log("pwz ainda existe?", !!window.pwz);
      
      // Aguardar um pouco mais para garantir que a limpeza foi executada
      setTimeout(() => {
        console.log("🔄 Redirecionando para login após limpeza e verificação");
        // Always navigate to login page and force a page refresh
        // This ensures complete cleanup of any remaining state
        window.location.href = '/login';
      }, 500);
    }
  };

  // Clean version of hasRole without excessive debugging
  const hasRoleCheck = useCallback((role: UserRole) => {
    return hasRole(role);
  }, [hasRole]);

  // Function to update the user in context
  const updateUserInContext = useCallback((updatedUser: AuthContextType["user"]) => {
    if (refreshUser) {
      refreshUser();
    }
  }, [refreshUser]);

  // Memoizar o value do context para evitar re-renders desnecessários
  const contextValue = useMemo(() => ({
    user, 
    session,
    isLoading: authLoading || isLoadingRoles, 
    login, 
    logout, 
    signup, 
    isSupabaseReady,
    roles,
    hasRole: hasRoleCheck,
    addRole,
    removeRole,
    updateUserInContext
  }), [
    user, 
    session, 
    authLoading, 
    isLoadingRoles, 
    login, 
    logout, 
    signup, 
    isSupabaseReady, 
    roles, 
    hasRoleCheck, 
    addRole, 
    removeRole, 
    updateUserInContext
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Re-export the User type for backward compatibility
export { type User } from "../types/auth";
